import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { createCheckout } from "@/lib/api";
import { toast } from "sonner";

const CartContext = createContext(null);

const UNIT_PRICE = 999; // cents
const SHIPPING = 419;
const FREE_THRESHOLD = 5000;
const TEAM_QTY = 15;
const TEAM_DISCOUNT_PCT = 10;
const STORAGE_KEY = "starsox_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const addItem = useCallback((color, label, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.color === color);
      if (existing) {
        return prev.map((i) => (i.color === color ? { ...i, quantity: Math.min(100, i.quantity + quantity) } : i));
      }
      return [...prev, { color, label, quantity }];
    });
    toast.success(`${label} zum Warenkorb hinzugefügt`, { description: "STARSOX Performance Grip Socks" });
    setOpen(true);
  }, []);

  const updateQty = useCallback((color, quantity) => {
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((i) => i.color !== color)
        : prev.map((i) => (i.color === color ? { ...i, quantity: Math.min(100, quantity) } : i))
    );
  }, []);

  const removeItem = useCallback((color) => {
    setItems((prev) => prev.filter((i) => i.color !== color));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = count * UNIT_PRICE;
  const teamDiscountApplied = count >= TEAM_QTY;
  // per-line rounding mirrors Stripe's coupon math
  const discount = teamDiscountApplied
    ? items.reduce((s, i) => s + Math.round(i.quantity * UNIT_PRICE * TEAM_DISCOUNT_PCT / 100), 0)
    : 0;
  const shipping = count === 0 ? 0 : subtotal >= FREE_THRESHOLD ? 0 : SHIPPING;
  const total = subtotal - discount + shipping;
  const remainingForFree = Math.max(0, FREE_THRESHOLD - subtotal);

  const checkout = useCallback(async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const payload = items.map((i) => ({ color: i.color, quantity: i.quantity }));
      const { checkout_url } = await createCheckout(payload);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error("Checkout fehlgeschlagen", { description: "Bitte versuche es erneut." });
      setLoading(false);
    }
  }, [items]);

  const value = useMemo(
    () => ({
      items, open, setOpen, addItem, updateQty, removeItem, clear,
      count, subtotal, discount, teamDiscountApplied, shipping, total, remainingForFree, checkout, loading,
      UNIT_PRICE, FREE_THRESHOLD, TEAM_QTY,
    }),
    [items, open, addItem, updateQty, removeItem, clear, count, subtotal, discount, teamDiscountApplied, shipping, total, remainingForFree, checkout, loading]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
